<template>
  <FilterMenu v-model="selectedItems" :items="filterItems" :small startSelected>
    <template #default="{ item }">
      <ContentFilterItem :item />
    </template>
  </FilterMenu>
</template>

<script lang="ts" setup>
import type { ISchema } from "~/models/IUserEntry";
import type {
  SchemaFilterItem,
  SchemaFilterValue,
} from "~/composables/entries/schema/useSchemaFilters";
import type { ISubscribedChannel } from "~/models/teamMode/IChannel";
import type { FilterMenuHeading } from "~/components/filter/FilterMenuTypes";

const { t } = useI18n({ useScope: "local" });
type Props = {
  schemas: ISchema[][];
};

type Emits = {
  filtered: [schemas: Props["schemas"]];
};

const props = defineProps<{
  channels: ISubscribedChannel[];
  schemas: ISchema[][];
  small?: boolean;
}>();

const emit = defineEmits<Emits>();

const { loggedIn } = storeToRefs(useUserStore());

const { curatedFilter, ownFilter, channelFilters } = useSchemaFilters<ISchema>(
  loggedIn,
  () => props.channels
);

const filterItems = computed(() => {
  const items: (
    | SchemaFilterItem<ISchema>
    | FilterMenuHeading<SchemaFilterValue<ISchema>>
  )[] = [curatedFilter.value, ownFilter.value];
  if (channelFilters.value.length) {
    items.push({
      title: t("broadcast_schemata"),
      heading: true,
      children: channelFilters.value,
    } as FilterMenuHeading<SchemaFilterValue<ISchema>>);
  }
  return items;
});

const selectedItems = ref<SchemaFilterItem<ISchema>[]>([]);
const selectedFilters = computed(() =>
  selectedItems.value.map(({ value }) => value.filter)
);

watchEffect(() => {
  const filteredSchemas = props.schemas.map((list) => {
    if (!list) {
      return [];
    }
    return list.filter((schema) =>
      selectedFilters.value.some((filter) => filter?.(schema))
    );
  });
  emit("filtered", filteredSchemas);
});
</script>
<i18n>
  {
    "de": {
      "broadcast_schemata": "Schemata aus Broadcasts"
    },
    "en": {
      "broadcast_schemata": "Other's content"
    }
  }
</i18n>
